import { useState } from "react";
import type { ApiClient } from "../../api/client";
import type { TermsStatus, TermsTexts } from "../../api/types";
import { t } from "../../i18n";
import { formatDate } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/toasts";
import { LegalDialog } from "./LegalDialog";
import { LEGAL_DOCUMENTS, TermsGate, type LegalDocument } from "./TermsGate";

interface Props {
  api: ApiClient;
  upcoming: NonNullable<TermsStatus["upcoming"]>;
  onClose: () => void;
}

/**
 * Tafo published a new version of the terms (prototype openAkkNotice): what changes, whether the owner must
 * accept again and until when the shop can cancel, the three texts to read; "Lezen en aanvaarden" opens the
 * texts to accept now.
 */
export function TermsNotice({ api, upcoming, onClose }: Props) {
  const text = t.terms.notice;
  const toast = useToast();
  const [reading, setReading] = useState(false);
  const [texts, setTexts] = useState<TermsTexts | null>(null);
  const [legal, setLegal] = useState<LegalDocument | null>(null);
  const from = formatDate(upcoming.effectiveFrom);

  // the newest version is the one announced
  const openLegal = (document: LegalDocument) => {
    if (texts !== null) {
      setLegal(document);
      return;
    }
    api
      .request<TermsTexts>("GET", "/device/terms", { retry: true })
      .then((loaded) => {
        setTexts(loaded);
        setLegal(document);
      })
      .catch(() => {
        toast(t.errors.unexpected, "bad");
      });
  };

  if (reading) return <TermsGate api={api} mode="new" onAccepted={onClose} onClose={onClose} />;
  return (
    <>
      <Dialog
        title={text.title(from)}
        subtitle={text.subtitle(upcoming.version)}
        size="narrow"
        onClose={onClose}
        footer={
          <>
            {/* the safe choice has the focus, not the first link of the texts */}
            <button type="button" className="btn" data-autofocus onClick={onClose}>
              {text.later}
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                setReading(true);
              }}
            >
              {text.read}
            </button>
          </>
        }
      >
        {upcoming.summary !== null && (
          <p>
            <b>{t.terms.whatChanges}</b> {upcoming.summary}
          </p>
        )}
        <p>
          {upcoming.requiresReacceptance ? text.reaccept(from) : text.nothing} {text.cancel(from)}
        </p>
        <p className="note legal-links">
          {LEGAL_DOCUMENTS.map((document, index) => (
            <span key={document}>
              {index > 0 && " · "}
              <button
                type="button"
                className="linkbtn"
                onClick={() => {
                  openLegal(document);
                }}
              >
                {t.terms.documents[document]}
              </button>
            </span>
          ))}
        </p>
      </Dialog>
      {legal !== null && texts !== null && (
        <LegalDialog
          texts={texts}
          document={legal}
          onClose={() => {
            setLegal(null);
          }}
        />
      )}
    </>
  );
}

const SEEN = "tafo.termsNotice";

/** Shown once per start-up of the till for each new version (sessionStorage: this window only). */
export function noticeDue(upcoming: TermsStatus["upcoming"]): upcoming is NonNullable<TermsStatus["upcoming"]> {
  if (upcoming === null) return false;
  try {
    return window.sessionStorage.getItem(SEEN) !== upcoming.version;
  } catch {
    return true;
  }
}

export function noticeSeen(version: string): void {
  try {
    window.sessionStorage.setItem(SEEN, version);
  } catch {
    // private mode: the notice may come back at the next start-up
  }
}
